/**
 * 中性色盤 —— 產品實際使用的那一套。
 *
 * 為什麼是手寫而不是由 seed color 產生
 * ------------------------------------
 * `createMd3Palette()`（見 palette.ts）能從一顆品牌色算出整套 MD3 色彩系統，
 * 那對「有品牌色的產品」很好用。但這個平台的視覺方向是**中性優先**：
 * 主要動作是近黑色，彩色只留給語意（狀態、錯誤），所以 primary 不是任何
 * 一顆 seed 衍生出來的結果，而是一個刻意選定的值。
 *
 * 因此這裡手寫兩套 `Md3ColorScheme`。要注意的是：
 *   - **角色名稱與介面完全沿用 MD3**，所以 app 端映射到 React Native Paper
 *     的那段程式不用改，未來要換回品牌色也只是換掉這個檔案。
 *   - `createMd3Palette()` 保留著並仍有測試，換品牌色時可以一鍵重算。
 *
 * 對比
 * ----
 * 每一組「文字色 / 底色」都在 neutral.test.ts 裡驗算 WCAG 對比：
 * 一般文字 >= 4.5:1，控制項邊框 >= 3:1。改動任何色值都會被那組測試擋下來。
 * 這一節不是裝飾性的要求 —— 內部系統的表格裡有大量 12–13px 的灰字
 * （編號、時間、欄位標題），淺灰字是最容易踩的可讀性地雷。
 */

import type { Md3ColorScheme, Md3Palette } from "./palette";

const light: Md3ColorScheme = {
  // 主要動作：近黑。整份設計裡唯一的「強調」手段
  primary: "#16181D",
  onPrimary: "#FFFFFF",
  primaryContainer: "#E8EAEE",
  onPrimaryContainer: "#16181D",

  // 次要：灰。用於次級文字與低調的填色
  secondary: "#5B6472",
  onSecondary: "#FFFFFF",
  secondaryContainer: "#EEF0F3",
  onSecondaryContainer: "#16181D",

  // 第三色在 MD3 裡是「點綴」，這裡借給 info 語意用
  tertiary: "#1A5499",
  onTertiary: "#FFFFFF",
  tertiaryContainer: "#E7EEF9",
  onTertiaryContainer: "#1A5499",

  error: "#A9231C",
  onError: "#FFFFFF",
  errorContainer: "#FBE9E7",
  onErrorContainer: "#A9231C",

  // 頁面底色是淺灰，卡片是純白 —— 層次由「白卡浮在灰底上」表達，不用陰影
  background: "#F3F4F6",
  onBackground: "#16181D",

  surface: "#FFFFFF",
  onSurface: "#16181D",
  surfaceVariant: "#EEF0F3",
  onSurfaceVariant: "#5B6472",

  // MD3 規定淺色模式下 Lowest 最亮、Highest 最暗，順序不能顛倒
  surfaceDim: "#E1E4E9",
  surfaceBright: "#FFFFFF",
  surfaceContainerLowest: "#FFFFFF",
  surfaceContainerLow: "#FAFBFC",
  surfaceContainer: "#F4F5F7",
  surfaceContainerHigh: "#EEF0F3",
  surfaceContainerHighest: "#E8EAEE",

  // outline 是「看得見的控制項邊框」（輸入框），outlineVariant 是純分隔線
  outline: "#8A929E",
  outlineVariant: "#E4E7EB",
  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#16181D",
  inverseOnSurface: "#F2F3F5",
  inversePrimary: "#F2F3F5",
};

const dark: Md3ColorScheme = {
  // 深色模式下主要動作反過來是近白
  primary: "#F2F3F5",
  onPrimary: "#16181D",
  primaryContainer: "#292E36",
  onPrimaryContainer: "#F2F3F5",

  secondary: "#A7AEB9",
  onSecondary: "#16181D",
  secondaryContainer: "#262B33",
  onSecondaryContainer: "#F2F3F5",

  tertiary: "#87B4F0",
  onTertiary: "#16181D",
  tertiaryContainer: "#16263B",
  onTertiaryContainer: "#87B4F0",

  error: "#F49189",
  onError: "#16181D",
  errorContainer: "#3B1815",
  onErrorContainer: "#F49189",

  background: "#0B0C0F",
  onBackground: "#F2F3F5",

  surface: "#16181D",
  onSurface: "#F2F3F5",
  surfaceVariant: "#22262D",
  onSurfaceVariant: "#A7AEB9",

  // 深色模式的階序與淺色相反：Lowest 最暗、Highest 最亮
  surfaceDim: "#0B0C0F",
  surfaceBright: "#31363F",
  surfaceContainerLowest: "#101216",
  surfaceContainerLow: "#171A1F",
  surfaceContainer: "#1C1F25",
  surfaceContainerHigh: "#22262D",
  surfaceContainerHighest: "#292E36",

  outline: "#6E7681",
  outlineVariant: "#2A2F37",
  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#F2F3F5",
  inverseOnSurface: "#16181D",
  inversePrimary: "#16181D",
};

/** 產品實際使用的色盤。 */
export const neutralPalette: Md3Palette = { light, dark };

/**
 * 語意狀態色。
 *
 * MD3 的角色只有 error，沒有 success / warning，而內部系統的表格幾乎一定
 * 需要一組「狀態徽章」的顏色。與其硬塞進 MD3 的角色裡（tertiary 當 info
 * 之類的），不如獨立一張表，命名用語意而不是業務名稱 ——
 * 「demo 的 pending 對應 warning」這種對照留在服務自己的程式碼裡。
 */
export interface StatusColor {
  /** 文字與圖示 */
  fg: string;
  /** 徽章底色 */
  bg: string;
}

export type StatusTone = "neutral" | "success" | "warning" | "info" | "danger";

export type StatusScheme = Record<StatusTone, StatusColor>;

export const statusColors: { light: StatusScheme; dark: StatusScheme } = {
  light: {
    neutral: { fg: "#5B6472", bg: "#EEF0F3" },
    success: { fg: "#0B6B47", bg: "#E4F4EC" },
    warning: { fg: "#7A4E00", bg: "#FBF0DC" },
    info: { fg: "#1A5499", bg: "#E7EEF9" },
    danger: { fg: "#A9231C", bg: "#FBE9E7" },
  },
  dark: {
    neutral: { fg: "#A7AEB9", bg: "#262B33" },
    success: { fg: "#54CBA0", bg: "#0F3327" },
    warning: { fg: "#E0AC4A", bg: "#33280F" },
    info: { fg: "#87B4F0", bg: "#16263B" },
    danger: { fg: "#F49189", bg: "#3B1815" },
  },
};
